import React, { useEffect, useState } from 'react';
import { Box, Button, IconButton, Paper, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { FormattedMessage } from 'react-intl';

const DISMISS_KEY = 'inescoInstallPromptDismissed';

/**
 * Non-intrusive "Add to Home Screen" banner for the PWA.
 *
 * - Android/Chromium: catches `beforeinstallprompt` and offers a native install
 *   button. (Note: Chromium only fires this event once a service worker with a
 *   fetch handler is registered — currently deferred, see src/serviceWorker.ts —
 *   so the Android button stays hidden until then. The component is inert, not broken.)
 * - iOS Safari: never fires the event, so we show manual Share → Add to Home
 *   Screen guidance instead.
 * - Hidden when already installed (standalone display-mode) or previously dismissed.
 */
function isInStandaloneMode(): boolean {
  return (
    window.matchMedia('(display-mode: standalone)').matches ||
    // iOS Safari exposes navigator.standalone
    (window.navigator as any).standalone === true
  );
}

function isIos(): boolean {
  return /iphone|ipad|ipod/i.test(window.navigator.userAgent);
}

function InstallPrompt() {
  const [visible, setVisible] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState<any>(null);
  const [iosHint, setIosHint] = useState(false);

  useEffect(() => {
    if (localStorage.getItem(DISMISS_KEY) === '1') return;
    if (isInStandaloneMode()) return;

    // Android / Chromium path.
    const onBeforeInstall = (e: Event) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setVisible(true);
    };
    window.addEventListener('beforeinstallprompt', onBeforeInstall);

    // iOS path — no event, show manual instructions.
    if (isIos()) {
      setIosHint(true);
      setVisible(true);
    }

    return () =>
      window.removeEventListener('beforeinstallprompt', onBeforeInstall);
  }, []);

  const dismiss = () => {
    setVisible(false);
    localStorage.setItem(DISMISS_KEY, '1');
  };

  const install = async () => {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    try {
      await deferredPrompt.userChoice;
    } finally {
      setDeferredPrompt(null);
      dismiss();
    }
  };

  if (!visible) return null;

  return (
    <Paper
      elevation={6}
      sx={{
        position: 'fixed',
        left: { xs: 8, sm: 16 },
        right: { xs: 8, sm: 16 },
        bottom: { xs: 8, sm: 16 },
        zIndex: (theme) => theme.zIndex.snackbar,
        p: 2,
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        maxWidth: 520,
        mx: 'auto'
      }}
    >
      <Box sx={{ flex: 1 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
          <FormattedMessage
            id="installPromptTitle"
            defaultMessage="Install inesco monitor"
          />
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {iosHint ? (
            <FormattedMessage
              id="installPromptIos"
              defaultMessage='Tap the Share button, then "Add to Home Screen".'
            />
          ) : (
            <FormattedMessage
              id="installPromptAndroid"
              defaultMessage="Add this app to your home screen for quick access."
            />
          )}
        </Typography>
      </Box>

      {!iosHint && deferredPrompt && (
        <Button
          variant="contained"
          onClick={install}
          sx={{
            textTransform: 'none',
            bgcolor: '#ffc04d',
            color: '#111111',
            '&:hover': { bgcolor: '#f7b34d' }
          }}
        >
          <FormattedMessage id="installPromptInstall" defaultMessage="Install" />
        </Button>
      )}

      <IconButton size="small" onClick={dismiss} aria-label="dismiss">
        <CloseIcon fontSize="small" />
      </IconButton>
    </Paper>
  );
}

export default InstallPrompt;
